import React, { createContext, useState, useEffect, useContext } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  deleteUser,
  sendPasswordResetEmail,
  User
} from 'firebase/auth';
import { auth } from '../config/firebase.config';
import { subscriptionService, SubscriptionStatus } from '../services/subscriptionService';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  subscriptionStatus: SubscriptionStatus;
  isSubscribed: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  deleteAccount: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  refreshSubscription: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus>({
    active: false,
    status: 'none',
  });

  useEffect(() => {
    console.log('[AuthContext] Setting up auth state listener');
    const authUnsubscribe = onAuthStateChanged(auth, (user) => {
      console.log('[AuthContext] Auth state changed:', user ? `User: ${user.email}` : 'No user (logged out)');
      setUser(user);
      setLoading(false);

      // Subscribe to subscription status when user logs in
      if (user) {
        console.log('[AuthContext] User logged in, subscribing to subscription status');
        subscriptionService.subscribeToStatus(user.uid, (status) => {
          console.log('[AuthContext] Subscription status updated:', status);
          setSubscriptionStatus(status);
        });
      } else {
        // Clean up subscription listener when user logs out
        console.log('[AuthContext] User logged out, cleaning up subscription listener');
        setSubscriptionStatus({
          active: false,
          status: 'none',
        });
      }
    });

    // Auto-refresh subscription when user returns to tab (e.g., from Stripe checkout)
    // Only set up if we're in a web environment
    const handleVisibilityChange = async () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible' && user) {
        console.log('[AuthContext] Tab became visible, refreshing subscription status...');
        try {
          const status = await subscriptionService.getSubscriptionStatus();
          setSubscriptionStatus(status);
          console.log('[AuthContext] Subscription refreshed on visibility change:', status);
        } catch (error) {
          console.error('[AuthContext] Failed to refresh subscription on visibility change:', error);
        }
      }
    };

    // Only add event listener if document is available (web only)
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    // Poll subscription status for 30 seconds after page load to catch returning Stripe users
    let pollCount = 0;
    const maxPolls = 15; // 15 polls x 2 seconds = 30 seconds
    const pollInterval = setInterval(async () => {
      if (user && pollCount < maxPolls) {
        pollCount++;
        console.log(`[AuthContext] Polling subscription status (${pollCount}/${maxPolls})...`);
        try {
          const status = await subscriptionService.getSubscriptionStatus();
          setSubscriptionStatus(status);
          // Stop polling if subscription becomes active
          if (status.active) {
            console.log('[AuthContext] Subscription active, stopping poll');
            clearInterval(pollInterval);
          }
        } catch (error) {
          console.error('[AuthContext] Failed to poll subscription:', error);
        }
      } else {
        clearInterval(pollInterval);
      }
    }, 2000);

    return () => {
      console.log('[AuthContext] Cleaning up auth state listener');
      authUnsubscribe();
      subscriptionService.cleanup();
      // Only remove event listener if document is available
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
      clearInterval(pollInterval);
    };
  }, [user]);

  const login = async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      console.error('Login error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to login';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signup = async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);
      await createUserWithEmailAndPassword(auth, email, password);
    } catch (err) {
      console.error('Signup error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to create account';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      console.log('[AuthContext] Starting logout...');
      setError(null);
      await signOut(auth);
      console.log('[AuthContext] Firebase signOut completed successfully');
    } catch (err) {
      console.error('[AuthContext] Logout error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to logout';
      setError(errorMessage);
      throw err;
    }
  };

  const deleteAccount = async () => {
    try {
      setError(null);
      if (!user) {
        throw new Error('No user is currently signed in');
      }
      setLoading(true);
      await deleteUser(user);
      setUser(null);
    } catch (err) {
      console.error('Delete account error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete account';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email: string) => {
    try {
      setError(null);
      setLoading(true);
      console.log('AuthContext: Sending password reset email to:', email);
      console.log('Auth instance:', auth);
      await sendPasswordResetEmail(auth, email);
      console.log('AuthContext: Password reset email sent successfully');
    } catch (err) {
      console.error('AuthContext: Password reset error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to send password reset email';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const clearError = () => setError(null);

  const refreshSubscription = async () => {
    if (!user) return;
    try {
      const status = await subscriptionService.getSubscriptionStatus();
      setSubscriptionStatus(status);
    } catch (err) {
      console.error('[AuthContext] Failed to refresh subscription status:', err);
    }
  };

  const isSubscribed = subscriptionStatus.active;

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        subscriptionStatus,
        isSubscribed,
        login,
        signup,
        logout,
        deleteAccount,
        resetPassword,
        refreshSubscription,
        clearError
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};